// ============================================================================
//  CrmAnalyticsPage.tsx — Analytics CRM avancés NexusCorp ERP
//  Bloc C — Funnel, vélocité, forecast, heatmap, podium commerciaux
// ============================================================================

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  FunnelChart,
  Funnel,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  AreaChart,
  Area,
} from 'recharts'
import {
  TrendingUp,
  TrendingDown,
  Target,
  Users,
  AlertTriangle,
  Activity,
  Award,
  Clock,
  ChevronUp,
  ChevronDown,
  BarChart2,
  Calendar,
} from 'lucide-react'
import api from '@/lib/axios'
import { fmtXOF, toNum } from '@/lib/decimal'

const Spinner = () => <div className="w-6 h-6 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
const fmtK = (v: number): string => {
  if (!v && v !== 0) return '0'
  if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M'
  if (Math.abs(v) >= 1_000) return (v / 1_000).toFixed(0) + 'k'
  return String(Math.round(v))
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface FunnelStage {
  stageId: string
  stageName: string
  order: number
  color: string
  isWon: boolean
  isLost: boolean
  count: number
  totalValue: number
  wonCount: number
  lostCount: number
  conversionRate: number
}

interface VelocityMonth {
  month: number
  monthLabel: string
  won: number
  lost: number
  created: number
  wonValue: number
  winRate: number
}

interface ForecastMonth {
  month: number
  monthLabel: string
  committed: number
  weighted: number
  best: number
}

interface HeatmapData {
  days: Array<{ day: number; date: string; count: number; types: Record<string, number> }>
  byType: Record<string, number>
  total: number
}

interface Performer {
  employeeId: string
  firstName: string
  lastName: string
  jobTitle?: string
  wonCount: number
  wonValue: number
  activityCount: number
}

interface StagnantOpp {
  id: string
  title: string
  amount: number
  scoreLevel: string
  daysSince: number
  stage: { name: string; color: string }
  assignedTo: { firstName: string; lastName: string } | null
}

interface AnalyticsData {
  year: number
  month: number
  funnel: FunnelStage[]
  velocity: VelocityMonth[]
  forecast: ForecastMonth[]
  activityHeatmap: HeatmapData
  topPerformers: Performer[]
  stagnantOpps: StagnantOpp[]
  wonLostRatio: VelocityMonth[]
  avgTimePerStage: Array<{
    stageId: string
    stageName: string
    color: string
    avgDays: number | null
  }>
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
  icon: Icon,
  color = 'blue',
  trend,
}: {
  label: string
  value: string | number
  sub?: string
  icon: any
  color?: string
  trend?: { value: number; label: string }
}) {
  const colors: Record<string, string> = {
    blue: 'text-[var(--blue-d)] bg-[var(--blue-bg)]',
    green: 'text-[var(--green-text)] bg-[var(--green-bg)]',
    amber: 'text-[var(--amber-text)] bg-[var(--amber-bg)]',
    red: 'text-[var(--red-text)] bg-[var(--red-bg)]',
    violet: 'text-[var(--mauve)] bg-[var(--mauve-bg)]',
  }
  const stripColors: Record<string, string> = {
    blue: 'var(--brand-orange)',
    violet: 'var(--brand-amber)',
    green: '#10B981',
    amber: '#F59E0B',
    red: '#EF4444',
    indigo: '#6366F1',
    teal: '#14B8A6',
    rose: '#F43F5E',
    orange: '#F97316',
    cyan: '#06B6D4',
  }
  const strip = stripColors[color] ?? 'var(--brand-orange)'
  return (
    <div
      className="nx-card p-4 space-y-2"
      style={{ borderTop: `3px solid ${strip}`, boxShadow: '0 1px 3px rgba(13,8,32,.04)' }}
    >
      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--tx3)]">{label}</p>
        <div className={`p-1.5 rounded-lg ${colors[color] ?? colors.blue}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
      </div>
      <p className="text-xl font-bold text-[var(--tx)]">{value}</p>
      {sub && <p className="text-xs text-[var(--tx3)]">{sub}</p>}
      {trend && (
        <div
          className={`flex items-center gap-1 text-xs ${trend.value >= 0 ? 'text-[var(--green-text)]' : 'text-[var(--red-text)]'}`}
        >
          {trend.value >= 0 ? (
            <ChevronUp className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3" />
          )}
          {Math.abs(trend.value)}% {trend.label}
        </div>
      )}
    </div>
  )
}

function SectionHeader({ title, icon: Icon }: { title: string; icon: any }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-[var(--blue-d)]" />
      <h2 className="text-sm font-semibold text-[var(--tx)]">{title}</h2>
    </div>
  )
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="font-medium text-[var(--tx)] mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name} : {typeof p.value === 'number' && p.value > 10000 ? fmtK(p.value) : p.value}
        </p>
      ))}
    </div>
  )
}

// ─── Heatmap activité ─────────────────────────────────────────────────────────

function ActivityHeatmap({ data }: { data: HeatmapData }) {
  const max = Math.max(1, ...data.days.map((d) => d.count))
  const weeks: Array<typeof data.days> = []
  let week: typeof data.days = []

  for (const day of data.days) {
    const dow = new Date(day.date).getDay()
    if (dow === 0 && week.length > 0) {
      weeks.push(week)
      week = []
    }
    week.push(day)
  }
  if (week.length > 0) weeks.push(week)

  const intensityColor = (count: number) => {
    if (count === 0) return '#ffffff'
    const intensity = count / max
    if (intensity < 0.25) return '#1d4ed8'
    if (intensity < 0.5) return '#2563eb'
    if (intensity < 0.75) return '#3b82f6'
    return '#60a5fa'
  }

  return (
    <div>
      <div className="flex gap-1 flex-wrap">
        {weeks.map((w, wi) => (
          <div key={wi} className="flex flex-col gap-1">
            {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((_, dow) => {
              const day = w.find((d) => new Date(d.date).getDay() === dow)
              return (
                <div
                  key={dow}
                  title={day ? `${day.date} : ${day.count} activités` : ''}
                  className="w-3.5 h-3.5 rounded-sm"
                  style={{ backgroundColor: day ? intensityColor(day.count) : 'var(--surface-2)' }}
                />
              )
            })}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-3 flex-wrap">
        {Object.entries(data.byType).map(([type, count]) => (
          <span key={type} className="text-[10px] text-[var(--tx3)]">
            {type}: <span className="text-[var(--tx)]">{count}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

// ─── Podium performers ────────────────────────────────────────────────────────

function PerformerCard({ performer, rank }: { performer: Performer; rank: number }) {
  const medals = ['🥇', '🥈', '🥉']
  const medal = medals[rank] ?? `#${rank + 1}`
  return (
    <div className="flex items-center gap-3 p-3 bg-white/60 rounded-lg">
      <span className="text-lg w-6 text-center">{medal}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-[var(--tx)] truncate">
          {performer.firstName} {performer.lastName}
        </p>
        <p className="text-[10px] text-[var(--tx3)]">{performer.jobTitle ?? 'Commercial'}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-xs font-bold text-[var(--green-text)]">{fmtK(performer.wonValue)} XOF</p>
        <p className="text-[10px] text-[var(--tx3)]">
          {performer.wonCount} deals · {performer.activityCount} activités
        </p>
      </div>
    </div>
  )
}

// ─── Stagnant Opps ────────────────────────────────────────────────────────────

// Couleur du score de l'opportunité (mêmes valeurs que la fiche opportunité)
const SCORE_COLORS: Record<string, string> = { HOT: '#DC2626', WARM: '#EA580C', COLD: 'var(--blue-d)' }

function StagnantRow({ opp }: { opp: StagnantOpp }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-[var(--bd)]">
      <div
        className="w-1.5 h-8 rounded-full shrink-0"
        style={{ backgroundColor: SCORE_COLORS[opp.scoreLevel] ?? '#64748b' }}
      />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-[var(--tx)] truncate">{opp.title}</p>
        <p className="text-[10px] text-[var(--tx3)] flex items-center gap-1">
          <span style={{ color: opp.stage.color }}>{opp.stage.name}</span>
          {opp.assignedTo && (
            <span>
              · {opp.assignedTo.firstName} {opp.assignedTo.lastName}
            </span>
          )}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-xs text-[var(--amber-text)]">{opp.daysSince}j sans activité</p>
        <p className="text-[10px] text-[var(--tx3)]">{fmtK(opp.amount)} XOF</p>
      </div>
    </div>
  )
}

// ============================================================================
//  PAGE PRINCIPALE
// ============================================================================

export default function CrmAnalyticsPage() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)

  const { data, isLoading } = useQuery<AnalyticsData>({
    queryKey: ['crm-analytics', year],
    queryFn: () =>
      api.get('/crm/analytics', { params: { year } }).then((r) => r.data.data ?? r.data),
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading || !data)
    return (
      <div className="flex items-center justify-center h-40">
        <Spinner />
      </div>
    )

  // KPIs synthétiques
  const totalWon = data.velocity.reduce((s, m) => s + m.won, 0)
  const totalLost = data.velocity.reduce((s, m) => s + m.lost, 0)
  const totalWonVal = data.velocity.reduce((s, m) => s + m.wonValue, 0)
  const globalWinRate =
    totalWon + totalLost > 0 ? Math.round((totalWon / (totalWon + totalLost)) * 100) : 0
  const forecastWeighted = data.forecast
    .filter((m) => m.month >= new Date().getMonth() + 1)
    .reduce((s, m) => s + m.weighted, 0)
  const activeOpps = data.funnel
    .filter((s) => !s.isWon && !s.isLost)
    .reduce((s, st) => s + st.count, 0)
  const pipelineValue = data.funnel
    .filter((s) => !s.isWon && !s.isLost)
    .reduce((s, st) => s + st.totalValue, 0)

  return (
    <div className="p-6 space-y-6 text-[var(--tx)]">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-[var(--blue-d)]" />
            Analytics CRM
          </h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Performance commerciale · {year}</p>
        </div>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="px-3 py-1.5 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--tx2)] focus:outline-none"
        >
          {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="Deals gagnés"
          value={totalWon}
          sub={fmtXOF(totalWonVal)}
          icon={Award}
          color="green"
        />
        <KpiCard
          label="Taux de succès"
          value={`${globalWinRate}%`}
          sub={`${totalLost} deals perdus`}
          icon={TrendingUp}
          color="blue"
        />
        <KpiCard
          label="Pipeline actif"
          value={activeOpps}
          sub={fmtXOF(pipelineValue)}
          icon={Target}
          color="violet"
        />
        <KpiCard
          label="Forecast pondéré"
          value={fmtK(forecastWeighted) + ' XOF'}
          sub="Opportunités à venir"
          icon={Calendar}
          color="amber"
        />
      </div>

      {/* Ligne 1 — Funnel + Vélocité */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Funnel de conversion */}
        <div className="nx-card p-4">
          <SectionHeader title="Funnel de conversion" icon={TrendingUp} />
          <div className="space-y-2">
            {data.funnel
              .filter((s) => !s.isLost)
              .map((stage, i) => {
                const maxCount = Math.max(1, ...data.funnel.map((s) => s.count))
                const pct = Math.round((stage.count / maxCount) * 100)
                return (
                  <div key={stage.stageId}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-[var(--tx2)]" style={{ color: stage.color }}>
                        {stage.stageName}
                      </span>
                      <span className="text-[var(--tx3)]">
                        {stage.count} · {fmtK(stage.totalValue)} XOF
                      </span>
                    </div>
                    <div className="h-5 bg-white rounded overflow-hidden relative">
                      <div
                        className="h-full rounded transition-all"
                        style={{
                          width: `${pct}%`,
                          backgroundColor: stage.color || '#3b82f6',
                          opacity: 0.8,
                        }}
                      />
                      {i > 0 && stage.conversionRate > 0 && (
                        <span className="absolute right-2 top-0.5 text-[10px] text-[var(--tx2)]">
                          ↓ {stage.conversionRate}%
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
          </div>
        </div>

        {/* Vélocité mensuelle */}
        <div className="nx-card p-4">
          <SectionHeader title="Vélocité mensuelle" icon={Activity} />
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.velocity} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="monthLabel" tick={{ fill: '#64748b', fontSize: 10 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="won" name="Gagnés" fill="#10b981" radius={[2, 2, 0, 0]} />
              <Bar dataKey="lost" name="Perdus" fill="#ef4444" radius={[2, 2, 0, 0]} />
              <Bar
                dataKey="created"
                name="Créés"
                fill="#3b82f6"
                radius={[2, 2, 0, 0]}
                opacity={0.5}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Ligne 2 — Forecast + Temps par étape */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Forecast */}
        <div className="nx-card p-4">
          <SectionHeader title="Forecast mensuel (XOF)" icon={TrendingUp} />
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data.forecast} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="monthLabel" tick={{ fill: '#64748b', fontSize: 10 }} />
              <YAxis tickFormatter={fmtK} tick={{ fill: '#64748b', fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="best"
                name="Best case"
                stroke="#60a5fa"
                fill="#60a5fa"
                fillOpacity={0.05}
                strokeWidth={1.5}
              />
              <Area
                type="monotone"
                dataKey="weighted"
                name="Pondéré"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.15}
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="committed"
                name="Committed"
                stroke="#10b981"
                fill="#10b981"
                fillOpacity={0.15}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Temps moyen par étape */}
        <div className="nx-card p-4">
          <SectionHeader title="Temps moyen par étape (jours)" icon={Clock} />
          <div className="space-y-2">
            {data.avgTimePerStage
              .filter((s) => s.avgDays !== null)
              .map((stage) => (
                <div key={stage.stageId} className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: stage.color || '#64748b' }}
                  />
                  <span className="text-xs text-[var(--tx3)] flex-1 truncate">{stage.stageName}</span>
                  <div className="flex items-center gap-2">
                    <div className="h-2 bg-white rounded overflow-hidden w-24">
                      <div
                        className="h-full rounded"
                        style={{
                          width: `${Math.min(100, ((stage.avgDays ?? 0) / 60) * 100)}%`,
                          backgroundColor: (stage.avgDays ?? 0) > 30 ? '#f59e0b' : '#3b82f6',
                        }}
                      />
                    </div>
                    <span className="text-xs font-mono text-[var(--tx)] w-8 text-right">
                      {stage.avgDays}j
                    </span>
                  </div>
                </div>
              ))}
            {data.avgTimePerStage.every((s) => s.avgDays === null) && (
              <p className="text-xs text-[var(--tx3)] text-center py-4">
                Pas assez de données historiques
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Ligne 3 — Heatmap + Top performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Heatmap activité */}
        <div className="nx-card p-4">
          <SectionHeader
            title={`Heatmap activité — ${data.activityHeatmap.total} ce mois`}
            icon={Activity}
          />
          <ActivityHeatmap data={data.activityHeatmap} />
        </div>

        {/* Top Performers */}
        <div className="nx-card p-4">
          <SectionHeader title="Top Performers" icon={Users} />
          {data.topPerformers.length === 0 ? (
            <p className="text-xs text-[var(--tx3)] text-center py-4">
              Aucun deal clôturé cette année
            </p>
          ) : (
            <div className="space-y-2">
              {data.topPerformers.slice(0, 5).map((p, i) => (
                <PerformerCard key={p.employeeId} performer={p} rank={i} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Ligne 4 — Won/Lost ratio + Opportunités stagnantes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Won/Lost ratio */}
        <div className="nx-card p-4">
          <SectionHeader title="Ratio Won/Lost mensuel" icon={TrendingDown} />
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.wonLostRatio} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="monthLabel" tick={{ fill: '#64748b', fontSize: 10 }} />
              <YAxis yAxisId="count" tick={{ fill: '#64748b', fontSize: 10 }} />
              <YAxis
                yAxisId="rate"
                orientation="right"
                tick={{ fill: '#64748b', fontSize: 10 }}
                domain={[0, 100]}
                unit="%"
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                yAxisId="count"
                type="monotone"
                dataKey="wonCount"
                name="Gagnés"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
              />
              <Line
                yAxisId="count"
                type="monotone"
                dataKey="lostCount"
                name="Perdus"
                stroke="#ef4444"
                strokeWidth={2}
                dot={false}
              />
              <Line
                yAxisId="rate"
                type="monotone"
                dataKey="winRate"
                name="Win rate %"
                stroke="#60a5fa"
                strokeWidth={1.5}
                dot={false}
                strokeDasharray="4 2"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Opportunités stagnantes */}
        <div className="nx-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[var(--amber-text)]" />
              <h2 className="text-sm font-semibold text-[var(--tx)]">Opportunités stagnantes</h2>
            </div>
            {data.stagnantOpps.length > 0 && (
              <span className="text-xs bg-[var(--amber-bg)] text-[var(--amber-text)] px-2 py-0.5 rounded-full">
                {data.stagnantOpps.length} à relancer
              </span>
            )}
          </div>
          {data.stagnantOpps.length === 0 ? (
            <p className="text-xs text-[var(--tx3)] text-center py-4">
              Aucune opportunité stagnante 🎉
            </p>
          ) : (
            <div className="max-h-52 overflow-y-auto space-y-0">
              {data.stagnantOpps.slice(0, 8).map((opp) => (
                <StagnantRow key={opp.id} opp={opp} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
